import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { hasConsentChoice, setConsent } from '../lib/cookie-consent';
import { navigate } from '../router';
import { boxSizingStyles } from '../styles/lit-shared';

@customElement('cookie-consent')
export class CookieConsent extends LitElement {
  @state() private visible = !hasConsentChoice();

  static styles = [
    boxSizingStyles,
    css`
      :host {
        position: fixed;
        inset-inline: 0;
        bottom: 0;
        z-index: 200;
        display: flex;
        justify-content: center;
        padding: var(--space-md);
        pointer-events: none;
        /* Avoid contributing to document overflow */
        max-width: 100%;
      }

      .banner {
        pointer-events: auto;
        width: 100%;
        max-width: 42rem;
        margin: 0;
        padding: var(--space-lg);
        border-radius: var(--radius-lg);
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        backdrop-filter: blur(24px);
        -webkit-backdrop-filter: blur(24px);
        box-shadow: var(--shadow-lg);
      }

      .banner p {
        margin: 0 0 var(--space-md);
        font-size: 0.875rem;
        line-height: 1.6;
        color: var(--color-text-muted);
        overflow-wrap: anywhere;
      }

      .banner a {
        color: var(--color-primary-light);
        text-decoration: underline;
        text-underline-offset: 2px;
      }

      .actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-sm);
      }

      .btn {
        padding: 0.5rem 1rem;
        border-radius: var(--radius-sm);
        font-size: 0.8125rem;
        font-weight: 600;
        cursor: pointer;
        border: 1px solid transparent;
        transition:
          background var(--duration-fast) var(--ease-out),
          transform var(--duration-fast) var(--ease-out);
      }

      .btn:hover {
        transform: translateY(-1px);
      }

      .btn--primary {
        background: var(--gradient-primary);
        color: #fff;
      }

      .btn--secondary {
        background: transparent;
        border-color: var(--color-border-strong);
        color: var(--color-text-muted);
      }

      @media (max-width: 639px) {
        :host {
          padding: var(--space-sm);
        }

        .banner {
          padding: var(--space-md);
        }

        .actions {
          flex-direction: column;
        }

        .btn {
          width: 100%;
          text-align: center;
        }
      }
    `,
  ];

  private acceptAll() {
    setConsent(true);
    this.visible = false;
  }

  private acceptNecessary() {
    setConsent(false);
    this.visible = false;
  }

  private goToCookiePolicy(e: Event) {
    e.preventDefault();
    void navigate('/politica-cookies');
  }

  render() {
    if (!this.visible) return '';

    return html`
      <div class="banner" role="dialog" aria-label="Consentimiento de cookies">
        <p>
          Usamos cookies técnicas imprescindibles y, si lo aceptas, cookies de analítica y de medición
          de anuncios (Google Analytics y Google Ads) para entender las visitas.
          <a href="/politica-cookies" @click=${this.goToCookiePolicy}>Política de Cookies</a>.
        </p>
        <div class="actions">
          <button class="btn btn--primary" type="button" @click=${this.acceptAll}>
            Aceptar todas
          </button>
          <button class="btn btn--secondary" type="button" @click=${this.acceptNecessary}>
            Solo necesarias
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cookie-consent': CookieConsent;
  }
}
